/*
 * PDP conversion fixes, site-wide (every product's buy box uses this
 * file):
 * 1. No size is chosen until the shopper picks one. On load the size
 *    pills look unselected and every Add to Bag button (main, the
 *    theme's sticky bar, quick view, and the Essential app's sticky
 *    bar) reads "Select a size" and looks disabled; tapping it doesn't
 *    add anything — it outlines the size grid in red with "Please
 *    select a size." (and scrolls it into view if needed). Picking a
 *    size switches the buttons to "Add to Bag". Dynamic checkout
 *    buttons (Shop Pay / Apple Pay / Google Pay), if enabled, stay
 *    hidden until then. Exceptions: products with a single size (no
 *    choice to make), and a page opened with ?variant= in the URL (ads,
 *    Shopping links), where that variant counts as chosen. Real
 *    unavailability (sold out) still disables the button.
 *    Important: the no-size-preselected part is purely
 *    visual/interaction-tracked, not a change to the underlying
 *    selected/checked state Liquid renders — an earlier version of
 *    this fix suppressed checked/selected server-side, which desynced
 *    the theme's own <variant-picker> web component from its own
 *    bootstrap state and silently broke clicking a new size entirely
 *    (confirmed by testing on a duplicate theme). So: the real default
 *    variant is still genuinely selected on load exactly as Shopify
 *    computes it (the component's internal state stays consistent,
 *    clicks keep working) — this file just (a) hides the LOOK of that
 *    default via a CSS class on <variant-picker> (see
 *    .is-awaiting-size-choice in size-finder.css), removed the moment
 *    a real click lands on a size pill/dropdown-item (also confirmed
 *    by testing: this component sets the underlying radio's .checked
 *    PROPERTY directly in its own click handling rather than via the
 *    browser's native label-activates-input flow, so a native 'change'
 *    listener never fires — click is what's actually observable), and
 *    (b) tracks "has this size actually been chosen" in a hasInteracted
 *    flag kept in plain JS, keyed by instanceId, NOT stored on the
 *    <variant-picker> node itself — that node gets replaced wholesale
 *    by the theme's own fetch+swap re-render a few hundred ms after a
 *    selection (refreshing variant image/inventory/price), which was
 *    silently wiping any state stored on it directly.
 * 2. Mirrors the top-of-page price into the buy-box price duplicate
 *    (product-form-block.liquid) whenever the original changes, so
 *    both stay correct on variant/size change regardless of how the
 *    theme's own variant-picker happens to update the DOM internally.
 */
(function () {
  // Loaded from layout/theme.liquid on every page (so collection-page
  // quick views get the size gate too — scripts inside quick view's
  // fetched HTML never run) and again by product-form-block.liquid on
  // product pages; only the first copy runs.
  if (window.__ntPdpBuyboxFixes) return;
  window.__ntPdpBuyboxFixes = true;

  // Tracks "has this product's size actually been chosen" per
  // instanceId in plain JS, deliberately NOT as a DOM attribute/class
  // on the <variant-picker> node itself. Confirmed by testing: this
  // theme's <variant-picker> replaces its own DOM (a fetch+swap re-
  // render, presumably to refresh variant image/inventory/price) a few
  // hundred ms after a selection click — any "already interacted"
  // marker stored ON that node is gone the moment it's replaced, which
  // re-triggered the "Select a size" gate right after a customer had
  // just genuinely picked one. This object survives that replacement
  // since it isn't attached to the node that gets discarded.
  var hasInteracted = {};
  // Last size the shopper tapped, per product form. The theme drops the
  // answer for a second size tapped while the first is still loading
  // (tap M then L quickly -> page stays on M while the URL says L), so
  // once loading settles the gate re-taps the size that was actually
  // chosen (max twice per tap).
  var lastTap = {};
  var lastTapRetries = {};

  // A page opened with ?variant= (ads, Google Shopping, shared links)
  // already names the exact size — read once, at load, before the
  // theme rewrites the URL on later selections.
  var openedWithVariant = /[?&]variant=\d+/.test(location.search);

  var SELECT_SIZE_LABEL = 'Select a size';
  var ADD_LABEL = 'Add to Bag';
  // Mobile shows the price in the button once a size is chosen.
  var ADD_LABEL_MOBILE = 'Add to cart';
  var SOLD_OUT_LABEL = 'Sold out';

  // Main product section (not quick view, whose id is "...__main---QuickView").
  function isMainInstance(instanceId) {
    return /---MainSection$/.test(instanceId);
  }

  function escapeHtml(t) {
    return String(t).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function currentPriceText(productId, instanceId) {
    var wrap = document.getElementById('product-price-' + productId + '-' + instanceId);
    var el = wrap && wrap.querySelector('.product-price__current-price');
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  // "Add to Bag" on desktop, "Add to cart · €81,95 EUR" on mobile (CSS
  // shows one of the two spans).
  function setReadyLabel(btn, productId, instanceId) {
    var textEl = btn.querySelector('[data-button-text]');
    if (!textEl) return;
    var price = currentPriceText(productId, instanceId);
    var html = '<span class="nt-btn-label--desktop">' + ADD_LABEL + '</span>' +
      '<span class="nt-btn-label--mobile">' + ADD_LABEL_MOBILE + (price ? ' · ' + escapeHtml(price) : '') + '</span>';
    if (textEl.innerHTML !== html) textEl.innerHTML = html;
    btn.setAttribute('aria-label', ADD_LABEL + (price ? ', ' + price : ''));
  }

  function initSizeGate(form) {
    var productId = form.getAttribute('data-product-id');
    var instanceId = form.getAttribute('data-instance-id');
    if (!productId || !instanceId) return;
    // Per product too: every quick view shares one instanceId.
    var gateKey = instanceId + ':' + productId;

    // Main buy button only — the sticky bars (theme + Essential app) are
    // deliberately left exactly as on the live theme.
    var buttons = [
      document.getElementById('product-buy-btn-' + productId + '-' + instanceId)
    ].filter(Boolean);
    if (!buttons.length) return;

    var picker = document.querySelector(
      'variant-picker[data-section="' + CSS.escape(instanceId) + '"]'
    );
    var sizeInputs = picker ? picker.querySelectorAll('[data-size-option-input]') : [];

    // No Size option, or only one size (caps, one-size items): nothing
    // to choose, so no gate — just the price-in-button label.
    if (sizeInputs.length < 2) {
      if (picker) picker.classList.remove('is-awaiting-size-choice');
      form.classList.remove('nt-size-pending');
      buttons.forEach(function (btn) {
        if (!btn.disabled) setReadyLabel(btn, productId, instanceId);
      });
      updateNotify(form, false);
      return;
    }

    if (openedWithVariant && isMainInstance(instanceId)) hasInteracted[gateKey] = true;

    var showSizeError = function () {
      picker.classList.add('pdp-size-error');
      var pillList = picker.querySelector('.product-variant-picker__pill-list');
      if (pillList && !picker.querySelector('.pdp-size-error-message')) {
        var msg = document.createElement('p');
        msg.className = 'pdp-size-error-message';
        msg.textContent = 'Please select a size.';
        pillList.insertAdjacentElement('afterend', msg);
      }
      // Short shake so a tap reads as "pick one here", and bring the
      // sizes into view if they're off screen.
      if (pillList) {
        pillList.classList.remove('pdp-size-shake');
        void pillList.offsetWidth;
        pillList.classList.add('pdp-size-shake');
        var r = pillList.getBoundingClientRect();
        if (r.top < 80 || r.bottom > window.innerHeight - 20) {
          pillList.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };
    picker.__ntShowSizeError = showSizeError;

    var clearSizeError = function () {
      picker.classList.remove('pdp-size-error');
      var msg = picker.querySelector('.pdp-size-error-message');
      if (msg) msg.remove();
    };

    var setGate = function (interacted) {
      picker.classList.toggle('is-awaiting-size-choice', !interacted);
      form.classList.toggle('nt-size-pending', !interacted);
      var soldOutChosen = false;
      buttons.forEach(function (btn) {
        var textEl = btn.querySelector('[data-button-text]');
        // Captured fresh on whichever button element is live right now
        // — including a button swapped in by the theme's own re-render
        // — so this always reflects THAT variant's real stock
        // availability, not a stale value from an earlier, possibly-
        // discarded button element.
        if (!btn.hasAttribute('data-select-size-original-html') && textEl) {
          btn.setAttribute('data-select-size-original-html', textEl.innerHTML);
        }
        if (!btn.hasAttribute('data-select-size-original-disabled')) {
          btn.setAttribute('data-select-size-original-disabled', btn.disabled ? 'true' : 'false');
        }

        var genuinelySoldOut = btn.getAttribute('data-select-size-original-disabled') === 'true';

        if (genuinelySoldOut && interacted) {
          // Real unavailability of a size the shopper actually picked —
          // restore the server's own "Sold out" text/state (the Notify
          // me form below takes over from here).
          if (textEl) textEl.innerHTML = btn.getAttribute('data-select-size-original-html');
          btn.classList.remove('is-awaiting-size-selection');
          btn.removeAttribute('aria-disabled');
          btn.disabled = true;
          soldOutChosen = true;
        } else if (!interacted) {
          // No size yet: looks disabled (grey) and says so, but stays
          // tappable so a tap can point the shopper at the size grid.
          if (textEl) textEl.textContent = SELECT_SIZE_LABEL;
          btn.setAttribute('aria-label', SELECT_SIZE_LABEL);
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('is-awaiting-size-selection');
          btn.disabled = false;
        } else {
          setReadyLabel(btn, productId, instanceId);
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-awaiting-size-selection');
          btn.disabled = false;
        }
      });
      updateNotify(form, soldOutChosen);
    };

    // Re-apply the CURRENT known state to every fresh picker node —
    // including ones swapped in by the theme's own re-render after a
    // genuine selection — rather than only setting it up once ever.
    setGate(!!hasInteracted[gateKey]);

    var want = lastTap[gateKey];
    if (want) {
      var checked = picker.querySelector('[data-size-option-input]:checked');
      var settled = !document.documentElement.hasAttribute('data-nt-variant-switching') &&
        !buttons.some(function (b) { return b.classList.contains('loading'); });
      if (checked && checked.value !== want && settled && (lastTapRetries[gateKey] || 0) < 2) {
        lastTapRetries[gateKey] = (lastTapRetries[gateKey] || 0) + 1;
        var again = Array.prototype.find.call(picker.querySelectorAll('[data-size-option-pill]'), function (l) {
          return l.getAttribute('data-value') === want;
        });
        if (again) setTimeout(function () { again.click(); }, 0);
      } else if (checked && checked.value === want) {
        lastTapRetries[gateKey] = 0;
      }
    }

    buttons.forEach(function (btn) {
      if (btn.hasAttribute('data-size-validation-bound')) return;
      btn.setAttribute('data-size-validation-bound', 'true');
      // Capture phase: needs to run and preventDefault before the
      // theme's own submit handling does anything with the click.
      btn.addEventListener(
        'click',
        function (e) {
          if (hasInteracted[gateKey]) {
            clearSizeError();
            return;
          }
          e.preventDefault();
          e.stopImmediatePropagation();
          showSizeError();
        },
        true
      );
    });

    if (picker.hasAttribute('data-size-gate-bound')) return;
    picker.setAttribute('data-size-gate-bound', 'true');

    // Listening for a native 'change' event on the radio doesn't work
    // here — the theme's own <variant-picker> component manages
    // selection by setting the .checked PROPERTY directly in its click
    // handler rather than letting the browser's native label-click-
    // activates-radio flow run its course, and a programmatic .checked
    // assignment doesn't fire 'change' (confirmed by testing: the pill
    // visibly became selected but no 'change' ever reached this
    // listener). Listening for the click itself, delegated to the
    // pill/dropdown-item labels, works regardless of how the component
    // manages the underlying input afterward.
    picker.addEventListener('click', function (e) {
      var pill = e.target && e.target.closest('[data-size-option-pill]');
      if (!pill) return;
      hasInteracted[gateKey] = true;
      if (e.isTrusted) {
        lastTap[gateKey] = pill.getAttribute('data-value');
        lastTapRetries[gateKey] = 0;
      }
      // Show the tapped size's real state at once (the theme marks sold-out
      // sizes with a no-available attribute) instead of waiting ~0.3s+ for
      // its re-render — no "Add to cart" -> "Sold out" flash, or back.
      var soldOut = pill.hasAttribute('no-available');
      buttons.forEach(function (btn) {
        btn.setAttribute('data-select-size-original-disabled', soldOut ? 'true' : 'false');
        if (soldOut) {
          var textEl = btn.querySelector('[data-button-text]');
          btn.setAttribute('data-select-size-original-html', SOLD_OUT_LABEL);
          if (textEl) textEl.textContent = SOLD_OUT_LABEL;
        }
      });
      setGate(true);
      clearSizeError();
    });
  }

  // A "Select a size" button tap is stopped here, at window capture —
  // the earliest point — rather than only on the button itself: apps
  // that bind their own listeners to add-to-cart buttons first (e.g.
  // EasyBundle, which sends a quick-view Add tap to a bundle builder)
  // otherwise act on it before the per-button handler above runs.
  if (!window.__ntSizeGateWindowGuard) {
    window.__ntSizeGateWindowGuard = true;
    window.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button[name="add"].is-awaiting-size-selection');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      var pf = btn.closest('product-form[data-instance-id]');
      var picker = pf && document.querySelector('variant-picker[data-section="' + CSS.escape(pf.getAttribute('data-instance-id')) + '"]');
      if (picker && picker.__ntShowSizeError) picker.__ntShowSizeError();
    }, true);
  }

  /* ---- Money formatting (same style as every price on the site) ---- */
  // "€9,05 EUR", "$12 USD": the visitor's own format, read from the money
  // sample snippets/cart-quick-view.liquid renders (1234.56); decimals
  // only when the amount isn't whole, like the theme's prices.
  function formatMoney(amount) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
    var root = document.getElementById('CartQuickViewRoot');
    var sample = root ? root.getAttribute('data-money-sample') || '' : '';
    var cents = Math.round(amount * 100);
    var whole = Math.floor(cents / 100);
    var frac = cents % 100;
    var m = sample.match(/1(\D?)234(?:(\D)(\d{2}))?/);
    if (!m) return (frac ? (cents / 100).toFixed(2) : String(whole)) + ' ' + currency;
    var num = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, m[1]);
    if (frac && m[2]) num += m[2] + (frac < 10 ? '0' : '') + frac;
    return sample.slice(0, m.index) + num + sample.slice(m.index + m[0].length);
  }

  /* ---- Sold-out size: "Notify me when back in stock" (Klaviyo) ---- */
  function klaviyoCompanyId() {
    var s = document.querySelector('script[src*="klaviyo.js?company_id="]');
    var m = s && s.src.match(/company_id=([A-Za-z0-9]+)/);
    return m ? m[1] : 'WxgQzW';
  }

  function updateNotify(form, show) {
    var holder = form.parentElement;
    if (!holder) return;
    var box = holder.querySelector(':scope > .nt-notify');
    if (!show) {
      if (box) box.hidden = true;
      return;
    }
    var idInput = form.querySelector('input[name="id"]');
    var variantId = idInput && idInput.value;
    if (!variantId) return;
    if (!box) {
      box = document.createElement('div');
      box.className = 'nt-notify';
      box.innerHTML =
        '<p class="nt-notify__title">This size is sold out</p>' +
        '<p class="nt-notify__text">Notify me when it\'s back in stock</p>' +
        '<form class="nt-notify__form" novalidate>' +
        '<input class="nt-notify__input" type="email" name="email" autocomplete="email" placeholder="Email address" aria-label="Email address" required>' +
        '<button class="nt-notify__btn btn btn--md" type="submit">Notify me</button>' +
        '</form>' +
        '<p class="nt-notify__msg" role="status" aria-live="polite"></p>';
      form.insertAdjacentElement('afterend', box);
      box.querySelector('form').addEventListener('submit', function (e) {
        e.preventDefault();
        var input = box.querySelector('.nt-notify__input');
        var msg = box.querySelector('.nt-notify__msg');
        var btn = box.querySelector('.nt-notify__btn');
        var setMsg = function (text, kind) {
          msg.textContent = text;
          msg.className = 'nt-notify__msg' + (kind ? ' is-' + kind : '');
        };
        // Autofill / paste can bring invisible characters; keep only the
        // address itself.
        var email = input.value.replace(/[\s\u200b\u00a0]+/g, '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setMsg('Please enter a valid email address.', 'error');
          return;
        }
        // The size that's selected right now (read at send time, never a
        // stale value from an earlier tap).
        var pf = box.parentElement && box.parentElement.querySelector('product-form');
        var idInput = pf && pf.querySelector('input[name="id"]');
        var vid = (idInput && idInput.value) || box.getAttribute('data-variant-id');
        if (!vid) {
          setMsg('Please choose a size first.', 'error');
          return;
        }
        btn.disabled = true;
        setMsg('Sending…');
        var payload = JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              channels: ['EMAIL']
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + vid } }
            }
          }
        });
        var send = function () {
          return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' + klaviyoCompanyId(), {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2024-10-15' },
            body: payload
          });
        };
        var fail = function (text, detail) {
          window.__ntBisError = { at: new Date().toISOString(), variant: vid, detail: detail };
          if (window.console && console.warn) console.warn('[back in stock]', detail);
          setMsg(text, 'error');
        };
        var handle = function (r) {
          if (r.ok) {
            box.querySelector('.nt-notify__form').hidden = true;
            setMsg("Thanks! We'll email you when this size is back.", 'success');
            return;
          }
          return r.text().then(function (t) {
            var detail = '';
            try { detail = (JSON.parse(t).errors || [])[0].detail || ''; } catch (err) {}
            if (/email/i.test(detail)) fail('Please check your email address and try again.', r.status + ' ' + detail);
            else fail("Sorry, we couldn't save that right now" + (detail ? ' (' + detail + ')' : '') + '.', r.status + ' ' + (detail || t.slice(0, 200)));
          });
        };
        send().then(handle, function () {
          // Network hiccup or a blocked request: one quiet retry.
          return new Promise(function (res) { setTimeout(res, 800); }).then(send).then(handle);
        }).catch(function (err) {
          fail("Couldn't connect. Please check your connection (or pause content blockers) and try again.", String(err && err.message || err));
        }).then(function () {
          btn.disabled = false;
        });
      });
    }
    if (box.getAttribute('data-variant-id') !== variantId) {
      // New sold-out size picked: fresh form.
      box.setAttribute('data-variant-id', variantId);
      box.querySelector('.nt-notify__form').hidden = false;
      var msgEl = box.querySelector('.nt-notify__msg');
      msgEl.textContent = '';
      msgEl.className = 'nt-notify__msg';
    }
    box.hidden = false;
  }

  /* ---- Free-shipping nudge under Add to Bag (main product, mobile) ---- */
  // Cart total = /cart.js total_price (after discounts, customer's
  // currency — what Shopify's free-shipping rate checks). It's fetched
  // once on load and again only when a cart drawer free-shipping bar's
  // data-cart-total changes (i.e. the cart changed), not on every DOM
  // change.
  var nudgeCart = { sig: null, total: 0, items: [] };
  var nudgeFetching = false;

  function cartSignature() {
    return Array.prototype.map.call(document.querySelectorAll('free-shipping-bar[data-cart-total]'), function (b) {
      return b.getAttribute('data-cart-total');
    }).join('|');
  }

  function refreshNudgeCart(sig) {
    if (nudgeFetching) return;
    nudgeFetching = true;
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) {
      nudgeCart = { sig: sig, total: (c.total_price || 0) / 100, items: (c.items || []).map(function (i) { return String(i.variant_id); }) };
    }).catch(function () {
      nudgeCart = { sig: sig, total: 0, items: [] };
    }).then(function () {
      nudgeFetching = false;
      renderNudge();
    });
  }

  function renderNudge() {
    var el = document.querySelector('product-form[data-instance-id$="---MainSection"] ~ [data-nt-fs-nudge]');
    if (!el) return;
    var pf = el.parentElement.querySelector('product-form[data-instance-id$="---MainSection"]');
    var sig = cartSignature();
    if (nudgeCart.sig !== sig) {
      refreshNudgeCart(sig);
      return;
    }
    var total = nudgeCart.total;
    var idInput = pf.querySelector('input[name="id"]');
    var variantId = idInput && idInput.value;
    var prices = {};
    try { prices = JSON.parse(el.parentElement.querySelector('[data-nt-variant-prices]').textContent); } catch (err) {}
    var price = (prices[variantId] || 0) / 100;
    var btn = pf.querySelector('[id^="product-buy-btn-"]');
    if (!btn || btn.disabled || !window.Shopify || !Shopify.currency) {
      el.hidden = true;
      return;
    }
    var rate = parseFloat(Shopify.currency.rate) || 1;
    var base = parseFloat(el.getAttribute('data-threshold')) || 0;
    if (!base) { el.hidden = true; return; }
    var threshold = window.ntFreeShippingThreshold ? window.ntFreeShippingThreshold(base, rate) : Math.ceil(base * rate - 1e-6);
    var inCart = nudgeCart.items.indexOf(String(variantId)) !== -1;
    var after = total + (inCart ? 0 : price);
    var remaining = Math.round((threshold - after) * 100) / 100;
    var text = remaining > 0 ? 'Add ' + formatMoney(remaining) + ' more for free shipping' : "You've unlocked free shipping";
    el.classList.toggle('is-unlocked', remaining <= 0);
    if (el.textContent !== text) el.textContent = text;
    el.hidden = false;
  }

  /* ---- Star rating near the price: tap opens this product's reviews ---- */
  // Same popup as the "Reviews · 26" row further down (this product's own
  // Loox reviews), instead of Loox's default link to a #looxReviewsFrame
  // anchor that doesn't exist on the page.
  if (!window.__ntRatingScroll) {
    window.__ntRatingScroll = true;
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[id$="__main"] .product__info .custom-app-block a[href^="#loox"]');
      if (!a) return;
      e.preventDefault();
      var row = document.querySelector('[id$="__main"] [data-buybox-rating-link][data-rating-popup-bound]');
      if (row) {
        row.click();
        return;
      }
      var fallback = document.querySelector('[id$="__main"] .pdp-ab-reviews-row') || document.getElementById('review-grid');
      if (fallback) fallback.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /* ---- Mobile gallery: "1/5" image counter ---- */
  function initMediaCounter() {
    var carousel = document.querySelector('[id$="__main"] .product-media-carousel-mobile__carousel');
    if (!carousel || carousel.__ntCounter || !('IntersectionObserver' in window)) return;
    carousel.__ntCounter = true;
    var badge = document.createElement('div');
    badge.className = 'nt-media-counter';
    badge.setAttribute('aria-hidden', 'true');
    badge.hidden = true;
    carousel.appendChild(badge);
    var ratios = new Map();
    var update = function () {
      // Only the slides actually in the slideshow (the theme hides other
      // colours' photos).
      var slides = Array.prototype.filter.call(carousel.querySelectorAll('.product-media-carousel-mobile__slide'), function (s) {
        return s.offsetWidth > 0;
      });
      if (slides.length < 2) { badge.hidden = true; return; }
      var best = -1, bestRatio = 0;
      slides.forEach(function (s, i) {
        var r = ratios.get(s) || 0;
        if (r > bestRatio) { bestRatio = r; best = i; }
      });
      if (best < 0) return; // gallery scrolled off screen: keep last value
      badge.hidden = false;
      badge.textContent = (best + 1) + '/' + slides.length;
    };
    // Page viewport as root: slides off to the side are clipped by the
    // carousel's overflow, so the one in view has the highest ratio.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { ratios.set(en.target, en.intersectionRatio); });
      update();
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    carousel.querySelectorAll('.product-media-carousel-mobile__slide').forEach(function (s) { io.observe(s); });
  }


  function initPriceSync() {
    document.querySelectorAll('[data-price-sync-target]').forEach(function (wrap) {
      if (wrap.hasAttribute('data-price-sync-bound')) return;

      var sourceId = wrap.getAttribute('data-price-sync-target');
      var source = document.getElementById(sourceId);
      var target = wrap.querySelector('.product-price-wrapper');
      if (!source || !target) return;

      wrap.setAttribute('data-price-sync-bound', 'true');

      // Copies only the reactive PARTS (current price, old/compare-at
      // price) — never wrap's own server-rendered outer markup. Found
      // and fixed a real bug here: innerHTML-replacing the whole thing
      // was stripping the outer .block-price wrapper this duplicate
      // needs (that class is what feeds it the real 18px/700 price
      // typography via CSS custom properties — see the block: price_block
      // comment in product-form-block.liquid) — confirmed via computed
      // style: it silently fell back to 16px/400 every time this ran,
      // immediately after an already-correct server render.
      var copy = function () {
        var srcCurrent = source.querySelector('.product-price__current-price');
        var tgtCurrent = target.querySelector('.product-price__current-price');
        if (srcCurrent && tgtCurrent) tgtCurrent.innerHTML = srcCurrent.innerHTML;

        var srcOld = source.querySelector('.product-price__old-price');
        var tgtOld = target.querySelector('.product-price__old-price');
        if (srcOld && tgtOld) {
          tgtOld.innerHTML = srcOld.innerHTML;
        } else if (srcOld && !tgtOld && tgtCurrent) {
          var clone = srcOld.cloneNode(true);
          tgtCurrent.insertAdjacentElement('afterend', clone);
        } else if (!srcOld && tgtOld) {
          tgtOld.remove();
        }
      };
      copy();

      var observer = new MutationObserver(copy);
      observer.observe(source, { childList: true, subtree: true, characterData: true });
    });
  }

  function initRatingReviewsPopup() {
    document.querySelectorAll('[data-buybox-rating-link]').forEach(function (link) {
      if (link.hasAttribute('data-rating-popup-bound')) return;

      var overlay = document.querySelector('[data-buybox-reviews-overlay]');
      var closeBtn = overlay ? overlay.querySelector('[data-buybox-reviews-close]') : null;
      var frame = overlay ? overlay.querySelector('[data-buybox-reviews-frame]') : null;
      if (!overlay || !closeBtn || !frame) return;

      link.setAttribute('data-rating-popup-bound', 'true');

      // Loox's reviews widget doesn't scroll within itself when
      // embedded (confirmed directly on its own document: html/body
      // both come back overflow:hidden, real content ~6800px tall
      // against a 946px viewport) — it's built to be told its true
      // content height and grown to fit, via a real postMessage it
      // sends ({type:"resize", height:N, frame:"looxReviewsFrame"}).
      // Matched by event.source (this exact iframe's window), not by
      // that "frame" id string — this page also has the separate
      // per-product #looxReviews widget elsewhere, which very likely
      // uses that same generic id, so id-matching risked resizing the
      // wrong iframe. The card itself is the thing that actually
      // scrolls (overflow-y:auto, capped at 85vh — see .buybox-reviews-modal
      // in product-form-block.liquid); this only ever grows the iframe
      // to its real height inside that scrollable card.
      window.addEventListener('message', function (e) {
        if (e.source !== frame.contentWindow) return;
        var data = e.data;
        try {
          if (typeof data === 'string') data = JSON.parse(data);
        } catch (err) {
          return;
        }
        if (data && data.type === 'resize' && data.height) {
          frame.style.height = data.height + 'px';
        }
      });

      // href stays a real link (/#review-grid — the homepage's own
      // all-store reviews section) as the no-JS fallback; this is what
      // actually makes it a popup instead of leaving the PDP, per
      // request. Loox's iframe src is only ever set on first open (not
      // in the Liquid markup) so a customer who never opens this never
      // pays for the extra request.
      var openModal = function () {
        if (!frame.getAttribute('src')) {
          var root = window.LOOX && window.LOOX.root;
          var clientId = window.LOOX && window.LOOX.clientId;
          var hash = window.LOOX && window.LOOX.hash;
          var productId = link.getAttribute('data-product-id');
          if (!root || !clientId) return false;
          // Per-product path (/reviews/{product_id}) so the popup shows
          // the same product this badge's count/rating is for, instead
          // of Loox's store-wide reviews widget.
          var path = '/widget/' + clientId + '/reviews' + (productId ? '/' + productId : '');
          frame.setAttribute('src', root + path + (hash ? '?h=' + hash : ''));
        }
        overlay.hidden = false;
        document.body.classList.add('buybox-reviews-modal-open');
        closeBtn.focus();
        return true;
      };

      var closeModal = function () {
        overlay.hidden = true;
        document.body.classList.remove('buybox-reviews-modal-open');
        link.focus();
      };

      link.addEventListener('click', function (e) {
        // window.LOOX not being ready yet (script still loading) is
        // the only case this doesn't handle — falls through to the
        // real /#review-grid link rather than opening an empty popup.
        if (openModal()) e.preventDefault();
      });

      closeBtn.addEventListener('click', closeModal);
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeModal();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !overlay.hidden) closeModal();
      });
    });
  }

  // Loox's own "Write a review" button inside the reviews popup injects
  // its form on the parent page (#looxOverlay_…, z-index 2147483646) —
  // one below this popup's overlay, so the form opened hidden behind it
  // and looked broken. Close the popup as soon as a Loox overlay appears.
  if (!window.__pdpLooxOverlayWatch) {
    window.__pdpLooxOverlayWatch = true;
    new MutationObserver(function (records) {
      var added = records.some(function (r) {
        return Array.prototype.some.call(r.addedNodes, function (n) {
          return typeof n.id === 'string' && n.id.indexOf('looxOverlay_') === 0;
        });
      });
      if (!added) return;
      document.querySelectorAll('[data-buybox-reviews-overlay]:not([hidden])').forEach(function (ov) {
        ov.hidden = true;
      });
      document.body.classList.remove('buybox-reviews-modal-open');
    }).observe(document.body, { childList: true });
  }

  // "Write a Review" button (product-form-block.liquid) — opens Loox's
  // review submission form directly rather than the reviews list popup.
  // Delegated so it survives the theme's variant-swap re-renders; the
  // window flag keeps it to one listener if this script loads twice.
  if (!window.__pdpWriteReviewBound) {
    window.__pdpWriteReviewBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-write-review]');
      if (!btn || !window.LOOX || typeof window.LOOX.showReviewForm !== 'function') return;
      e.preventDefault();
      window.LOOX.showReviewForm(btn.getAttribute('data-product-id'));
    });
  }

  // Products whose gallery is rebuilt per colour (data-nt-gallery-colour,
  // product-variant-picker-block.liquid — the Unisex Essential Tee
  // Bundle): the theme rebuilt the gallery on EVERY option change, so
  // switching 1x / 2x / 3x or size reloaded the same photos (fade/zoom
  // flash). Only rebuild when the colour actually changes.
  function initGallerySwapGuard() {
    document.querySelectorAll('variant-picker[data-nt-gallery-colour]').forEach(function (vp) {
      if (vp.__ntSwapGuard || typeof vp.shouldSwapProduct !== 'function') return;
      vp.__ntSwapGuard = true;
      var original = vp.shouldSwapProduct;
      vp.shouldSwapProduct = function (newPicker) {
        if (newPicker && newPicker.getAttribute('data-nt-gallery-colour') === vp.getAttribute('data-nt-gallery-colour') &&
            newPicker.dataset.url === vp.dataset.url) {
          return false;
        }
        return original.call(vp, newPicker);
      };
    });
  }

  // "Recommended" tag over the size-finder's suggested size
  // (.pill.is-recommended-size::after, size-finder.css) is wider than a
  // size box and centred on it, so on the first / last box it ran past
  // the product container's edge and was clipped. Shift it just enough to
  // stay inside (--nt-reco-shift, pdp-mobile-buybox.css); the arrow stays
  // on the box.
  function fitRecommendedTags() {
    document.querySelectorAll('.pill.is-recommended-size').forEach(function (pill) {
      var box = pill.closest('.product__container, .quick-view__content, variant-picker');
      if (!box) return;
      var tagW = parseFloat(getComputedStyle(pill, '::after').width) || 0;
      var pr = pill.getBoundingClientRect(), br = box.getBoundingClientRect();
      if (!tagW || !pr.width) return;
      var left = pr.left + pr.width / 2 - tagW / 2;
      var shift = 0;
      if (left < br.left) shift = br.left - left;
      else if (left + tagW > br.right) shift = br.right - (left + tagW);
      var val = Math.round(shift) + 'px';
      if (pill.style.getPropertyValue('--nt-reco-shift') !== val) pill.style.setProperty('--nt-reco-shift', val);
    });
  }
  if (!window.__ntRecoFit) {
    window.__ntRecoFit = true;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].target.classList && records[i].target.classList.contains('is-recommended-size')) { fitRecommendedTags(); return; }
      }
    }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', fitRecommendedTags);
  }

  // When the theme swaps in a new gallery on a colour change (products
  // with data-product-media-filtering-option: the Unisex Essential Tee and
  // Tee Bundle), the new gallery's arrows and thumbnails stayed wired to
  // the carousel engine that was being replaced (they connect when they're
  // set up, the carousel builds its engine a moment later) and the new
  // engine came up on the wrong axis, so they did nothing / showed as
  // disabled. For swapped galleries only, the engine is put back on its
  // axis, and arrow and
  // thumbnail clicks drive the live carousel directly, and the arrows'
  // disabled state and the selected thumbnail (is-primary) follow it.
  function mainCarouselOf(container) {
    var car = container.querySelector('.product-media-carousel__carousel');
    return car && car.embla && container.contains(car.embla.rootNode()) ? car : null;
  }

  function syncSwappedGallery(container) {
    var car = mainCarouselOf(container);
    if (!car) return;
    var e = car.embla;
    var idx = e.selectedScrollSnap();
    container.querySelectorAll('.product-thumbnail-carousel__thumb').forEach(function (t, i) {
      t.classList.toggle('is-primary', i === idx);
    });
    container.querySelectorAll('.product-media-carousel__nav-btn').forEach(function (btn) {
      var can = btn.getAttribute('data-direction') === 'prev' ? e.canScrollPrev() : e.canScrollNext();
      if (can) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', '');
    });
  }

  // Puts a main-product carousel engine back on the axis its markup asks
  // for (data-axis) if it came up on another one — happens to the gallery
  // swapped in on a colour change (and could on a slow first load).
  function healCarouselAxis(car) {
    try {
      var want = car.getAttribute('data-axis');
      var engine = want && car.embla && car.embla.internalEngine && car.embla.internalEngine();
      if (engine && engine.options && engine.options.axis !== want) {
        car.embla.reInit({ axis: want });
        return true;
      }
    } catch (err) {}
    return false;
  }

  function watchSwappedGallery(container, tries) {
    var car = mainCarouselOf(container);
    if (!car) {
      if (tries < 100 && container.isConnected) setTimeout(function () { watchSwappedGallery(container, tries + 1); }, 100);
      return;
    }
    // Keep checking for a few seconds: the theme can replace the engine
    // again right after the swap.
    var checks = 0;
    var tick = function () {
      var c = mainCarouselOf(container);
      if (c) {
        healCarouselAxis(c);
        if (!c.embla.__ntSynced) {
          c.embla.__ntSynced = true;
          c.embla.on('select', function () { syncSwappedGallery(container); });
          c.embla.on('reInit', function () { syncSwappedGallery(container); });
        }
        syncSwappedGallery(container);
      }
      if (++checks < 16 && container.isConnected) setTimeout(tick, 250);
    };
    tick();
  }

  if (!window.__ntCarouselAxisLoadCheck) {
    window.__ntCarouselAxisLoadCheck = true;
    window.addEventListener('load', function () {
      document.querySelectorAll('[id$="__main"] .product-desktop-media .product-media-carousel__carousel').forEach(healCarouselAxis);
    });
  }

  if (!window.__ntGallerySwapFix) {
    window.__ntGallerySwapFix = true;
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes, function (n) {
          if (n.nodeType === 1 && n.matches('[data-product-media-container]') && n.closest('[id$="__main"]')) {
            n.setAttribute('data-nt-swapped', '');
            watchSwappedGallery(n, 0);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', function (ev) {
      var container = ev.target.closest && ev.target.closest('[data-nt-swapped]');
      if (!container) return;
      var nav = ev.target.closest('.product-media-carousel__nav-btn');
      var thumb = !nav && ev.target.closest('.product-thumbnail-carousel__thumb');
      if (!nav && !thumb) return;
      var car = mainCarouselOf(container);
      if (!car) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      if (nav) {
        if (nav.getAttribute('data-direction') === 'prev') car.embla.scrollPrev(); else car.embla.scrollNext();
      } else {
        var thumbs = Array.prototype.slice.call(container.querySelectorAll('.product-thumbnail-carousel__thumb'));
        car.embla.scrollTo(thumbs.indexOf(thumb));
      }
    }, true);
  }

  // Colour switch on the Tee / Tee Bundle (per-colour gallery): no jump,
  // no blank photo.
  //  - The theme swaps in a new gallery whose carousel starts a moment
  //    later; until then it renders taller (desktop: ~990px instead of
  //    ~710px) and the page jumped. The gallery holder keeps its height
  //    from the click until the new carousel is running.
  //  - Every colour's photos are preloaded once the page has settled, at
  //    the same URLs/sizes the gallery uses, so a switch shows the photo
  //    straight away instead of loading it then (phones: ~0.4s blank).
  if (!window.__ntColourSwitchSmooth) {
    window.__ntColourSwitchSmooth = true;
    // Desktop: while the theme swaps in the new gallery, its first frames
    // are unsized (photo shown bigger and shifted over the thumbnails for
    // ~150ms). So the current gallery is kept on screen as a still copy
    // over the same spot, the new one is hidden underneath at the old
    // height, and the copy is removed once the new one has settled.
    // (Held via a class on <html> + a CSS variable because the theme
    // replaces the gallery holder itself.)
    var ntCover = null;
    var ntHold = function (on) {
      var root = document.documentElement;
      if (on) {
        var el = document.querySelector('[id$="__main"] .product-desktop-media');
        var r = el && el.getBoundingClientRect();
        if (!r || !r.height || !r.width) return;
        if (ntCover) ntCover.remove();
        // Plain <img> copies of what's on screen (not a clone of the theme's
        // markup, which the theme's own swap code would find and rewrite).
        ntCover = document.createElement('div');
        ntCover.setAttribute('aria-hidden', 'true');
        ntCover.setAttribute('data-nt-cover', '');
        ntCover.style.cssText = 'position:absolute;margin:0;left:' + (r.left + window.scrollX) + 'px;top:' + (r.top + window.scrollY) + 'px;width:' + r.width + 'px;height:' + r.height + 'px;overflow:hidden;pointer-events:none;z-index:2;background:' + (getComputedStyle(document.body).backgroundColor || '#fff') + ';';
        el.querySelectorAll('img').forEach(function (img) {
          var ir = img.getBoundingClientRect();
          if (ir.width < 2 || ir.height < 2 || ir.bottom <= r.top || ir.top >= r.bottom || ir.right <= r.left || ir.left >= r.right) return;
          if (getComputedStyle(img).visibility === 'hidden' || getComputedStyle(img).opacity === '0') return;
          // Only what's really visible: clip to the image's own carousel
          // window (the next slide sits just below it, hidden by the theme).
          var win = img.closest('.carousel__viewport, [data-carousel-viewport], .embla');
          if (win) {
            var wr = win.getBoundingClientRect();
            var visW = Math.min(ir.right, wr.right) - Math.max(ir.left, wr.left);
            var visH = Math.min(ir.bottom, wr.bottom) - Math.max(ir.top, wr.top);
            if (visW <= 0 || visH <= 0 || (visW * visH) / (ir.width * ir.height) < 0.9) return;
          }
          var cs = getComputedStyle(img);
          var holder = img.closest('.product-thumbnail-carousel__thumb, .product-media__image-wrapper') || img;
          var hcs = getComputedStyle(holder);
          var c = document.createElement('img');
          c.src = img.currentSrc || img.src;
          c.alt = '';
          c.style.cssText = 'position:absolute;display:block;left:' + (ir.left - r.left) + 'px;top:' + (ir.top - r.top) + 'px;width:' + ir.width + 'px;height:' + ir.height + 'px;object-fit:' + cs.objectFit + ';object-position:' + cs.objectPosition + ';border-radius:' + hcs.borderRadius + ';';
          ntCover.appendChild(c);
        });
        el.querySelectorAll('.product-thumbnail-carousel__thumb.is-primary').forEach(function (th) {
          var tr = th.getBoundingClientRect();
          var o = document.createElement('div');
          o.style.cssText = 'position:absolute;left:' + (tr.left - r.left) + 'px;top:' + (tr.top - r.top) + 'px;width:' + tr.width + 'px;height:' + tr.height + 'px;box-shadow:inset 0 0 0 1px #141416;border-radius:' + getComputedStyle(th).borderRadius + ';';
          ntCover.appendChild(o);
        });
        // The yellow USP icon row under the photos (plain markup + SVGs):
        // copied as-is, minus its data-* hooks so no script picks it up.
        el.querySelectorAll('[data-product-usps]').forEach(function (u) {
          var ur = u.getBoundingClientRect();
          if (!ur.width || !ur.height) return;
          var cu = u.cloneNode(true);
          [cu].concat(Array.prototype.slice.call(cu.querySelectorAll('*'))).forEach(function (n) {
            Array.prototype.slice.call(n.attributes).forEach(function (at) {
              if (at.name.indexOf('data-') === 0 || at.name === 'id') n.removeAttribute(at.name);
            });
          });
          cu.style.position = 'absolute';
          cu.style.margin = '0';
          cu.style.left = (ur.left - r.left) + 'px';
          cu.style.top = (ur.top - r.top) + 'px';
          cu.style.width = ur.width + 'px';
          cu.style.maxWidth = ur.width + 'px';
          ntCover.appendChild(cu);
        });
        document.body.appendChild(ntCover);
        root.style.setProperty('--nt-gallery-hold', r.height + 'px');
        root.classList.add('nt-gallery-hold');
      } else {
        root.classList.remove('nt-gallery-hold');
        if (ntCover) { ntCover.remove(); ntCover = null; }
      }
    };
    if (!document.getElementById('nt-gallery-hold-css')) {
      var st = document.createElement('style');
      st.id = 'nt-gallery-hold-css';
      st.textContent = '@media (min-width: 768px){html.nt-gallery-hold [id$="__main"] .product-desktop-media{height:var(--nt-gallery-hold)!important;min-height:var(--nt-gallery-hold)!important;max-height:var(--nt-gallery-hold)!important;overflow:hidden!important;visibility:hidden!important;}}';
      document.head.appendChild(st);
    }
    // Release once the NEW gallery is in, its carousel is running, its USP
    // row is built and it's back to its normal size. Measured with the hold
    // still on (scrollHeight = real content height).
    var ntReleaseRaf = null;
    var ntRelease = function (oldEl) {
      cancelAnimationFrame(ntReleaseRaf);
      var started = performance.now();
      var lastH = -1, stable = 0;
      var oldH = oldEl ? oldEl.getBoundingClientRect().height : 0;
      // Where the old USP icon row sat (left / width): the new row is
      // first laid out ~40px off and only lines up once product-usps.js has
      // matched it to the photo, so the hold also waits for that.
      var uspBox = function (root) {
        var u = root && Array.prototype.slice.call(root.querySelectorAll('[data-product-usps]')).filter(function (x) { return x.getBoundingClientRect().width > 0; })[0];
        return u ? u.getBoundingClientRect() : null;
      };
      var oldUsp = uspBox(oldEl);
      // Thumbnails in view must have loaded (otherwise the column shows a
      // single thumbnail for a moment).
      var thumbsOk = function (root) {
        var ok = true;
        root.querySelectorAll('.product-thumbnail-carousel__thumb img').forEach(function (img) {
          var r = img.getBoundingClientRect();
          if (r.width > 1 && r.height > 1 && r.bottom > 0 && r.top < window.innerHeight && !(img.complete && img.naturalWidth > 0)) ok = false;
        });
        return ok;
      };
      var step = function () {
        var el = document.querySelector('[id$="__main"] .product-desktop-media');
        var cont = document.querySelector('[id$="__main"] [data-product-media-container][data-nt-swapped]');
        var swapped = el && el !== oldEl && cont;
        var car = swapped && mainCarouselOf(cont);
        var uspOk = !cont || !cont.querySelector('[data-product-usps]') || cont.querySelector('[data-product-usps] [data-product-usps-row] > *');
        var h = el ? el.scrollHeight : 0;
        // The new gallery first lays out ~1120px tall (carousel not sized
        // yet) and can sit there ~100ms, so wait until it's back to normal
        // size (<= old height + 40px) for 2 frames; 1.5s safety limit.
        var settled = swapped && car && car.embla && uspOk && h > 0 && h <= oldH + 40;
        if (settled && oldUsp) {
          var nu = uspBox(el);
          if (!nu || Math.abs(nu.left - oldUsp.left) > 3 || Math.abs(nu.width - oldUsp.width) > 3) settled = false;
        }
        if (settled && !thumbsOk(el)) settled = false;
        stable = settled ? stable + 1 : 0;
        lastH = h;
        if (stable >= 2 || performance.now() - started > 1500) {
          ntHold(false);
          return;
        }
        ntReleaseRaf = requestAnimationFrame(step);
      };
      ntReleaseRaf = requestAnimationFrame(step);
    };
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var vp = t.closest('variant-picker[data-nt-gallery-colour]');
      if (!vp || !/color|colour/i.test(t.name || '')) return;
      if (t.value === vp.getAttribute('data-nt-gallery-colour')) return;
      if (!window.matchMedia('(min-width: 768px)').matches) return;
      var oldEl = document.querySelector('[id$="__main"] .product-desktop-media');
      ntHold(true);
      ntRelease(oldEl);
    }, true);

    var ntPreload = function () {
      var vp = document.querySelector('variant-picker[data-nt-gallery-colour]');
      if (!vp || window.__ntPreloaded) return;
      window.__ntPreloaded = true;
      var handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (!handle) return;
      // URL patterns the gallery actually uses (main photo + thumbnails).
      var patterns = [];
      document.querySelectorAll('[id$="__main"] [data-product-media-container] img').forEach(function (img) {
        var u = img.currentSrc || img.src;
        var m = u && u.match(/^(.*\/files\/)[^/?]+\?(?:.*&)?(width=\d+)/);
        if (m && patterns.indexOf(m[1] + '|' + m[2]) === -1) patterns.push(m[1] + '|' + m[2]);
      });
      if (!patterns.length) return;
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          (p.media || []).forEach(function (m) {
            var src = m.src || (m.preview_image && m.preview_image.src);
            var f = src && src.match(/\/files\/([^?]+)(?:\?v=(\d+))?/);
            if (!f) return;
            patterns.forEach(function (pt) {
              var parts = pt.split('|');
              var img = new Image();
              img.decoding = 'async';
              img.src = parts[0] + f[1] + '?' + (f[2] ? 'v=' + f[2] + '&' : '') + parts[1];
            });
          });
        })
        .catch(function () {});
    };
    var ntIdle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1500); };
    window.addEventListener('load', function () { ntIdle(ntPreload); });
  }

  // Country / currency list search: the theme only matched the start of the
  // country name ("Sing"). Also match anywhere in the name ("land"), the
  // currency code ("sgd", "sek") and the country code ("se"). Runs after
  // the theme's own handler on the same input.
  if (!window.__ntLocalizationSearch) {
    window.__ntLocalizationSearch = true;
    document.addEventListener('input', function (e) {
      var field = e.target;
      if (!field.matches || !field.matches('[data-localization-search-field]')) return;
      var box = field.closest('localization-selector');
      if (!box) return;
      var q = field.value.trim().toLowerCase();
      box.querySelectorAll('[data-localization-item][data-country]').forEach(function (item) {
        var name = (item.getAttribute('data-country') || '').toLowerCase();
        var label = (item.getAttribute('aria-label') || '').toLowerCase();
        var parts = label.split(' ');
        var currency = parts[parts.length - 1] || '';
        var code = (item.getAttribute('data-value') || '').toLowerCase();
        var match = !q || name.indexOf(q) !== -1 || currency.indexOf(q) === 0 || code === q;
        item.classList.toggle('hidden', !match);
      });
    });
  }

  function init() {
    document.querySelectorAll('product-form[data-product-id]').forEach(initSizeGate);
    renderNudge();
    initMediaCounter();
    initGallerySwapGuard();
    fitRecommendedTags();
    initPriceSync();
    initRatingReviewsPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Loading finished (pdp-buybox-fixes' variant-switch watcher below):
  // re-check the gate, including the "page landed on the wrong size" case.
  document.addEventListener('nt:variant-settled', function () { setTimeout(init, 30); });

  // Re-run after Shopify's own section/quick-view re-renders swap in
  // fresh markup (same watch-and-debounce pattern size-finder.js uses
  // for the same reason — new product-form/variant-picker pairs can
  // appear after the initial page-load scan).
  var pending = false;
  // When the theme swaps in a fresh buy button / size picker (it does on
  // every size click), re-apply the gate right away: MutationObserver
  // callbacks run before the next paint, so the shopper never sees the
  // raw server button flash in between. Everything else stays debounced.
  function touchesBuyBox(records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches('variant-picker, [id^="product-buy-btn-"], product-form') ||
            n.querySelector('variant-picker, [id^="product-buy-btn-"]')) return true;
      }
    }
    return false;
  }
  var mo = new MutationObserver(function (records) {
    if (touchesBuyBox(records)) {
      init();
      return;
    }
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      init();
    }, 150);
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();

/* Instant feedback when switching variants with the option pills (e.g. "1 Unit"
   -> "3 Units (save 33%)", sizes, packs). The theme fetches the new variant's
   section HTML before it moves its own `selected` class and re-enables the
   button, so for ~0.5s the old pill stayed highlighted and "Add to Bag" faded
   into its loading spinner — read as a glitch. Now the clicked pill is
   highlighted immediately, and the button keeps its normal look (still
   disabled, so nothing can be added until the new variant is ready). The real
   add-to-cart loading state is untouched. */
(function () {
  if (window.__ntInstantPillSelect) return;
  window.__ntInstantPillSelect = true;

  var style = document.createElement('style');
  style.textContent =
    '[data-nt-variant-switching] .product-form__btn.loading{opacity:1!important;}' +
    '[data-nt-variant-switching] .product-form__btn.loading [data-button-text]{visibility:visible!important;opacity:1!important;}' +
    '[data-nt-variant-switching] .product-form__btn.loading .btn__loading-spinner{display:none!important;}' +
    // Snap the pill highlight instead of a colour fade (mid-fade the label was grey on grey).
    'variant-picker label.pill,variant-picker label.pill *{transition:none!important;}' +
    // A not-yet-placed copy of the "Write a Review" button (see pdp-layout.js) stays invisible.
    '[data-write-review-slot]:not([data-placed]){display:none!important;}';
  document.head.appendChild(style);

  var clearTimer = null;
  function markSwitching() {
    document.documentElement.setAttribute('data-nt-variant-switching', '');
    var started = Date.now();
    clearInterval(clearTimer);
    clearTimer = setInterval(function () {
      var busy = document.querySelector('product-form .product-form__btn.loading');
      if (!busy || Date.now() - started > 8000) {
        clearInterval(clearTimer);
        document.documentElement.removeAttribute('data-nt-variant-switching');
        document.dispatchEvent(new CustomEvent('nt:variant-settled'));
        // An Add tap made while the new size was still loading (slow
        // connections take a few seconds) is carried out now, on the
        // fresh button, instead of being silently ignored.
        if (queuedAddId) {
          var fresh = document.getElementById(queuedAddId);
          queuedAddId = null;
          if (fresh && !fresh.disabled && !fresh.classList.contains('is-awaiting-size-selection')) fresh.click();
        }
      }
    }, 80);
  }

  var queuedAddId = null;
  window.addEventListener('click', function (e) {
    if (!document.documentElement.hasAttribute('data-nt-variant-switching')) return;
    var btn = e.target.closest && e.target.closest('[id^="product-buy-btn-"]');
    if (!btn || btn.classList.contains('is-awaiting-size-selection')) return;
    if (btn.getAttribute('data-select-size-original-disabled') === 'true') return; // sold out
    e.preventDefault();
    e.stopImmediatePropagation();
    queuedAddId = btn.id;
  }, true);

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio' || !input.closest || !input.closest('variant-picker')) return;
    var label = input.id && document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]');
    if (label && label.classList.contains('pill')) {
      var list = label.parentElement;
      if (list) Array.prototype.forEach.call(list.querySelectorAll('label.pill.selected'), function (l) { l.classList.remove('selected'); });
      label.classList.add('selected');
    }
    markSwitching();
  }, true);
})();
